import { createContext, FC, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useLazyQuery, useQuery } from '@apollo/client'
import { reviewById } from '../__generated__/reviewById'
import { NEXT_REVIEW, REVIEW_BY_ID } from '../queries/reviewQuery'
import { createReview } from '../lib/createReview'
import { IReview } from '../components/types'
import { nextReview, nextReviewVariables } from '../__generated__/nextReview'
import { SortOrder } from '../__generated__/globalTypes'

interface IReviewDetailContext {
  handlerClose: () => void
  review: IReview | null
  getAnotherReview: (sortOrder: SortOrder) => Promise<void>
  loading: boolean
  hasPrevReview: boolean
  hasNextReview: boolean
  id
  handlerSlide: (id: string) => void
}

export const ReviewDetailContext = createContext({} as IReviewDetailContext)

const ReviewDetailProvider: FC<{ productId?: string }> = ({
  children,
  productId,
}) => {
  const [id, setId] = useState(null)
  const [currentOrder, setCurrentOrder] = useState(null)
  const [hasNextReview, setHasNextReview] = useState(true)
  const [hasPrevReview, setHasPrevReview] = useState(true)
  const router = useRouter()
  const { query, pathname } = router
  const { data, loading } = useQuery<reviewById>(REVIEW_BY_ID, {
    variables: { id },
    skip: !id,
  })
  const review = data?.element ? createReview(data.element) : null
  const [getNextReview, { data: nextReviewData, refetch: refetchNextReview }] =
    useLazyQuery<nextReview, nextReviewVariables>(NEXT_REVIEW)

  useEffect(() => {
    if (query?.detailReview) {
      setId(query.detailReview)
    } else {
      setId(null)
    }
  }, [query])

  useEffect(() => {
    const newId = nextReviewData?.elements?.nodes?.[1]?.id
    const { file, ...rest } = router.query

    if (newId) {
      router.push(
        {
          pathname: router.pathname,
          query: { ...rest, detailReview: newId },
        },
        null,
        {
          scroll: false,
          shallow: true,
        },
      )
    }
  }, [nextReviewData])

  useEffect(() => {
    checkNext()
  }, [currentOrder, nextReviewData])

  function checkNext() {
    setHasNextReview(true)
    setHasPrevReview(true)

    if (nextReviewData?.elements?.nodes.length > 2) {
      setHasNextReview(true)
      setHasPrevReview(true)
    } else if (currentOrder !== null) {
      currentOrder === SortOrder.desc
        ? setHasNextReview(false)
        : setHasPrevReview(false)
    }
  }

  async function getAnotherReview(sortOrder: SortOrder) {
    setCurrentOrder(sortOrder)

    const reviewVariables: nextReviewVariables = {
      where: {
        active: { equals: true },
        propertyValuesRelation: {
          some: { property: { code: { in: ['PHOTOS', 'VIDEOS'] } } },
        },
      },
      id,
      sortOrder,
    }
    if (productId) {
      reviewVariables.where = {
        active: { equals: true },
        AND: [
          {
            propertyValuesRelation: {
              some: {
                property: { code: { equals: 'ELEMENT' } },
                targetElement: { id: { equals: productId } },
              },
            },
          },
          {
            propertyValuesRelation: {
              some: { property: { code: { in: ['PHOTOS', 'VIDEOS'] } } },
            },
          },
        ],
      }
    }

    nextReviewData
      ? refetchNextReview(reviewVariables)
      : getNextReview({ variables: reviewVariables })
  }

  function handlerClose() {
    setId(null)

    if (query?.detailReview) {
      const { detailReview, file, ...rest } = query

      router.push(
        {
          pathname,
          query: rest,
        },
        null,
        {
          scroll: false,
          shallow: true,
        },
      )
    }
  }

  function handlerSlide(id: string) {
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, file: id },
      },
      null,
      {
        scroll: false,
        shallow: true,
      },
    )
  }

  return (
    <ReviewDetailContext.Provider
      value={{
        review,
        handlerClose,
        getAnotherReview,
        loading,
        hasNextReview,
        hasPrevReview,
        id,
        handlerSlide,
      }}
    >
      {children}
    </ReviewDetailContext.Provider>
  )
}

export default ReviewDetailProvider
